import { Constants } from "@eagerdog/constants";
import { download } from "src/components/Download/DownloadManager";

interface ShowInfo {
  _id: string;
  show_type: string;
  showName?: any;
}

interface EventInfo {
  _id: string;
  sanctioning_club: string;
}

export interface Action {
  label: string;
  onClick: () => void;
}

interface BuildActionsParams {
  isAdmin: boolean;
  event?: EventInfo;
  showInfo: ShowInfo;
  eid?: string;
  props?: { eid?: string };
  setDLShow?: (id: string) => void;
  setModalContent?: (modal: string) => void;
}

const commonActions = {
  akcScoresheets: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Scoresheets",
    onClick: () =>
      download.pushDownload({
        type: "akc_scoresheets",
        eventId: event!._id,
        showId: showInfo._id,
      }),
  }),
  akcScoresheetscondensed: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Scoresheets (Condensed)",
    onClick: () =>
      download.pushDownload({
        type: "akc_scoresheets_condensed",
        eventId: event!._id,
        showId: showInfo._id,
      }),
  }),
  catScoresheet: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Scoresheet",
    onClick: () =>
      download.pushDownload({
        type: "cat_scoresheet",
        eventId: event!._id,
        showId: showInfo._id,
      }),
  }),
  fastcatScoresheetFormatted: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Scoresheets (Formatted)",
    onClick: () =>
      download.pushDownload({
        type: "fastcat_scoresheet",
        eventId: event!._id,
        showId: showInfo._id,
      }),
  }),
  fastdashScoresheetFormatted: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Scoresheets (Formatted)",
    onClick: () =>
      download.pushDownload({
        type: "fastdash_scoresheet",
        eventId: event!._id,
        showId: showInfo._id,
      }),
  }),
  akcResultsXml: ({ event }: BuildActionsParams): Action => ({
    label: "Download AKC Results XML",
    onClick: () => download.pushDownload({ type: "akc_results", eventId: event!._id }),
  }),
  checkin: ({ event, showInfo, eid }: BuildActionsParams, allShows = false): Action => ({
    label: "Download Check-in List",
    onClick: () =>
      download.pushDownload({
        type: "checkin",
        eventId: event!._id,
        showId: allShows ? "*" : showInfo._id,
      }),
  }),
  judgeBooks: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Judges Book",
    onClick: () =>
      download.pushDownload({ type: "judge_books", eventId: event!._id, showId: showInfo._id }),
  }),
  dockDivingCompetitorList: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Competitor List",
    onClick: () =>
      download.pushDownload({ type: "judge_books", eventId: event!._id, showId: showInfo._id }),
  }),
  noseworkJudgeBooks: ({ event, showInfo, setDLShow, setModalContent }: BuildActionsParams): Action => ({
    label: "Download Judges Book",
    onClick: () => {
      setDLShow?.(showInfo._id);
      setModalContent?.("noseworkJudgeBooks");
    },
  }),
  herdingScoresheets: ({ event, showInfo, setDLShow, setModalContent }: BuildActionsParams): Action => ({
    label: "Download Scoresheets",
    onClick: () => {
      setDLShow?.(showInfo._id);
      setModalContent?.("herdingScoresheetsModal");
    },
  }),
  herdingSecretaryCertification: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Secretary Certification",
    onClick: () =>
      download.pushDownload({
        type: "herding_secretary_certification",
        eventId: event!._id,
        showId: showInfo._id,
      }),
  }),
  herdingHighInTrial: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download High In Trial Report",
    onClick: () =>
      download.pushDownload({
        type: "herding_high_in_trial",
        eventId: event!._id,
        showId: showInfo._id,
      }),
  }),
  herdingCatalog: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Catalog",
    onClick: () =>
      download.pushDownload({
        type: "herding_catalog",
        eventId: event!._id,
        showId: showInfo._id,
      }),
  }),
  precisionCoursingJudgeBooks: ({ event, showInfo, setDLShow, setModalContent }: BuildActionsParams): Action => ({
    label: "Download Judges Book",
    onClick: () => {
      setDLShow?.(showInfo._id);
      setModalContent?.("precisionCoursingJudgeBooks");
    },
  }),
  judgeBooksMatch: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Judges Book (Match)",
    onClick: () =>
      download.pushDownload({ type: "judge_books_match", eventId: event!._id, showId: showInfo._id }),
  }),
  judgeBooksGroup: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Group Judges Book",
    onClick: () =>
      download.pushDownload({ type: "judge_books_group", eventId: event!._id, showId: showInfo._id }),
  }),
  judgeBreeds: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Judge Breeds",
    onClick: () =>
      download.pushDownload({ type: "judge_breeds", eventId: event!._id, showId: showInfo._id }),
  }),
  results: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Results",
    onClick: () =>
      download.pushDownload({ type: "results", eventId: event!._id, showId: showInfo._id }),
  }),
  fastCatResultsDetailed: ({ event }: BuildActionsParams): Action => ({
    label: "Download Results (detailed)",
    onClick: () =>
      download.pushDownload({ type: "fast_cat_results_detailed", eventId: event!._id }),
  }),
  ringSheets: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Ring Sheets",
    onClick: () =>
      download.pushDownload({ type: "ring-sheets", eventId: event!._id, showId: showInfo._id }),
  }),
  ringSheetsFormatted: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Ring Sheets (formatted)",
    onClick: () =>
      download.pushDownload({
        type: "ring-sheets",
        eventId: event!._id,
        showId: showInfo._id,
        builder_suffix: "formatted",
      }),
  }),
  scoresheets: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Scoresheets",
    onClick: () => download.pushDownload({ type: "scoresheets", eventId: event!._id, showId: showInfo._id }),
  }),
  trial: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Trial Report",
    onClick: () => download.pushDownload({ type: "trial", eventId: event!._id, showId: showInfo._id }),
  }),
  counts: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Counts",
    onClick: () => download.pushDownload({ type: "counts", eventId: event!._id, showId: showInfo._id }),
  }),
  countsFormatted: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Counts (Formatted)",
    onClick: () => download.pushDownload({ type: "counts_formatted", eventId: event!._id, showId: showInfo._id }),
  }),
  expectingTitleCounts: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Expecting Title Counts",
    onClick: () => download.pushDownload({ type: "counts", eventId: event!._id, showId: showInfo._id }),
  }),
  juniorParticipationReport: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Junior Participation Report",
    onClick: () => download.pushDownload({ type: "junioir_participation_report", eventId: event!._id, showId: showInfo._id }),
  }),
  highInTrial: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download High in Trial Report",
    onClick: () => download.pushDownload({ type: "hit_trial", eventId: event!._id, showId: showInfo._id }),
  }),
  championshipPoints: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Championship Points Report",
    onClick: () => download.pushDownload({ type: "championship_points", eventId: event!._id, showId: showInfo._id }),
  }),
  juniorHandlerCertificate: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Junior Handler Certificate",
    onClick: () => download.pushDownload({ type: "junior_handler_certificate", eventId: event!._id, showId: showInfo._id }),
  }),
  ukcNoseworkScoresheets: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Scoresheets (UKC)",
    onClick: () =>
      download.pushDownload({
        type: "ukc_scoresheets",
        eventId: event!._id,
        showId: showInfo._id,
        builder_suffix: "2",
      }),
  }),
  nwtExport: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download NWT Export",
    onClick: () => download.pushDownload({ type: "nwt", eventId: event!._id, showType: showInfo.show_type }),
  }),
  ribbonLabels: ({ showInfo, setDLShow, setModalContent }: BuildActionsParams): Action => ({
    label: "Download Ribbon Labels",
    onClick: () => {
      setDLShow?.(showInfo._id);
      setModalContent?.("ribbonSkipModal");
    },
  }),
  fastcatRibbonLabels: ({ showInfo, setDLShow, setModalContent }: BuildActionsParams): Action => ({
    label: "Download Ribbon Labels (5160)",
    onClick: () => {
      setDLShow?.(showInfo._id);
      setModalContent?.("ribbonSkipModalSmall");
    },
  }),
  scoresheetLabels: ({ showInfo, setDLShow, setModalContent }: BuildActionsParams): Action => ({
    label: "Download Scoresheet Labels",
    onClick: () => {
      setDLShow?.(showInfo._id);
      setModalContent?.("scoresheetLabelsModal");
    },
  }),
  showAwards: ({ showInfo, setDLShow, setModalContent }: BuildActionsParams): Action => ({
    label: "Show Awards",
    onClick: () => {
      setDLShow?.(showInfo._id);
      setModalContent?.("showAwardsModal");
    },
  }),
  noDownloads: (): Action => ({
    label: "No downloads available for this event type",
    onClick: () => {
      // Do nothing - this is just a placeholder
    },
  }),
  measurementReport: ({ event, showInfo }: BuildActionsParams): Action => ({
    label: "Download Measurement Report",
    onClick: () =>
      download.pushDownload({
        type: "measurement_report",
        eventId: event!._id,
        showId: showInfo._id,
      }),
  }),
};

type BuilderFn = (params: BuildActionsParams) => Action;

const actionBuilders: Record<string, Record<string, BuilderFn[]>> = {
  [Constants.sanctioning_club.UKC]: {
    [Constants.show_type.weight_pull]: [commonActions.judgeBooks, commonActions.trial],
    [Constants.show_type.agility]: [commonActions.checkin, commonActions.judgeBooks, commonActions.scoresheets, commonActions.trial, commonActions.highInTrial, commonActions.scoresheetLabels, commonActions.juniorParticipationReport],
    [Constants.show_type.precision_coursing]: [commonActions.precisionCoursingJudgeBooks, commonActions.trial],
    [Constants.show_type.obedience]: [commonActions.judgeBooks, commonActions.ringSheets, commonActions.scoresheets, commonActions.trial, commonActions.highInTrial, commonActions.juniorParticipationReport],
    [Constants.show_type.rally_obedience]: [commonActions.judgeBooks, commonActions.ringSheets, commonActions.scoresheets, commonActions.trial, commonActions.juniorParticipationReport],
    [Constants.show_type.shed_dog]: [commonActions.judgeBooks, commonActions.scoresheets],
    [Constants.show_type.nosework]: [commonActions.trial, commonActions.nwtExport, commonActions.checkin, commonActions.scoresheets, commonActions.ribbonLabels, commonActions.results, commonActions.noseworkJudgeBooks, commonActions.judgeBooksMatch, commonActions.ukcNoseworkScoresheets],
    [Constants.show_type.conformation]: [commonActions.judgeBooks, commonActions.judgeBreeds, commonActions.judgeBooksGroup, commonActions.counts, commonActions.countsFormatted, commonActions.trial],
    [Constants.show_type.dock_jumping]: [commonActions.judgeBooks, commonActions.trial],
    [Constants.show_type.fast_cat]: [commonActions.results, commonActions.expectingTitleCounts],
  },
  [Constants.sanctioning_club.AKC]: {
    [Constants.show_type.agility]: [commonActions.scoresheets, commonActions.trial, commonActions.measurementReport, commonActions.juniorHandlerCertificate],
    [Constants.show_type.scentwork]: [(params) => commonActions.checkin(params), commonActions.scoresheets, commonActions.trial, commonActions.juniorHandlerCertificate, commonActions.akcResultsXml],
    [Constants.show_type.obedience]: [commonActions.judgeBooks, commonActions.checkin, commonActions.ringSheets, commonActions.ringSheetsFormatted, commonActions.trial, commonActions.highInTrial, commonActions.juniorHandlerCertificate],
    [Constants.show_type.rally_obedience]: [commonActions.judgeBooks, commonActions.checkin, commonActions.ringSheets, commonActions.ringSheetsFormatted, commonActions.scoresheets, commonActions.scoresheetLabels, commonActions.trial, commonActions.juniorHandlerCertificate],
    [Constants.show_type.conformation]: [commonActions.judgeBooks, commonActions.counts, commonActions.trial],
    [Constants.show_type.fast_cat]: [commonActions.fastcatScoresheetFormatted, commonActions.akcScoresheets, commonActions.akcScoresheetscondensed, commonActions.results, commonActions.fastCatResultsDetailed, commonActions.fastcatRibbonLabels, commonActions.expectingTitleCounts, commonActions.trial, commonActions.juniorHandlerCertificate],
    [Constants.show_type.cat]: [commonActions.checkin, commonActions.catScoresheet, commonActions.results],
    [Constants.show_type.lure_coursing]: [commonActions.scoresheets],
    [Constants.show_type.fetch]: [(params) => commonActions.checkin(params, true), commonActions.scoresheets, commonActions.results, commonActions.counts],
    [Constants.show_type.canine_good_citizen]: [],
    [Constants.show_type.att]: [commonActions.scoresheets, commonActions.judgeBooks],
    [Constants.show_type.farm_dog]: [commonActions.scoresheets, commonActions.results],
    [Constants.show_type.trick_dog]: [commonActions.scoresheets],
    [Constants.show_type.herding]: [commonActions.herdingScoresheets, commonActions.herdingSecretaryCertification, commonActions.herdingHighInTrial, commonActions.herdingCatalog, commonActions.trial, commonActions.judgeBooks, commonActions.championshipPoints, commonActions.juniorHandlerCertificate],
    [Constants.show_type.pre_trial_herding]: [commonActions.herdingScoresheets, commonActions.herdingCatalog, commonActions.trial, commonActions.judgeBooks, commonActions.championshipPoints],
    [Constants.show_type.instinct_test_herding]: [commonActions.herdingScoresheets, commonActions.herdingCatalog, commonActions.trial, commonActions.judgeBooks, commonActions.championshipPoints],
  },
  [Constants.sanctioning_club.PWDCA]: {
    [Constants.show_type.water_dog]: [commonActions.checkin, commonActions.results],
  },
  [Constants.sanctioning_club.BHA]: {
    [Constants.show_type.barn_hunt]: [commonActions.scoresheets, commonActions.fastcatRibbonLabels, commonActions.results],
  },
  [Constants.sanctioning_club.ABI]: {},
  [Constants.sanctioning_club.ISWS]: {
    [Constants.show_type.fast_dash]: [commonActions.fastdashScoresheetFormatted],
  },
  [Constants.sanctioning_club.GCR]: {},
  [Constants.sanctioning_club.CPE]: {},
  [Constants.sanctioning_club.GENERAL]: {
    [Constants.show_type.sniff_n_go]: [commonActions.noDownloads],
    [Constants.show_type.on_leash_run_through]: [commonActions.noDownloads],
    [Constants.show_type.obedience_show_n_go]: [commonActions.noDownloads],
    [Constants.show_type.general_rally]: [commonActions.noDownloads],
    [Constants.show_type.dock_diving]: [commonActions.dockDivingCompetitorList],
  },
  [Constants.sanctioning_club.FH]: {
    [Constants.show_type.agility]: [commonActions.noDownloads],
  },
  [Constants.sanctioning_club.AHBA]: {
    [Constants.show_type.herding]: [commonActions.scoresheets, commonActions.trial, commonActions.judgeBooks],
  },
  default: {
    default: [commonActions.judgeBooks]
  }
};

export function buildActions(params: BuildActionsParams): Action[] {
  const { isAdmin, event, showInfo } = params;
  if (!isAdmin || !event) return [];

  const club = event.sanctioning_club;
  const showType = showInfo.show_type;

  const builders: BuilderFn[] = actionBuilders[club]?.[showType] ?? actionBuilders["default"]["default"];
  return builders.map((builder) => builder(params));
}
